import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { Progress } from "../components/ui/progress"
import { cn } from "../lib/utils"

export function ProgressRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  // Theme-v2 atom defaults: theme-variant/size surfaced on data-* attrs only.
  // Note: legacy progress blocks overload `element.variant` as a numeric
  // value carrier (see `rawSource = element.value ?? element.variant`
  // below); that semantic is preserved and is separate from the theme-v2
  // data-variant contract. The theme-variant flows from atom defaults
  // only — there is no "explicit element.variant wins" case for progress.
  const atomDefaults = useAtomDefaults("progress")
  const resolvedVariant = atomDefaults.defaultVariant ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  // Read value from element.value (preferred) or element.variant (legacy)
  const rawSource = element.value ?? element.variant ?? "0"
  const rawValue = String(resolveSymbol(String(rawSource)) ?? "0")
  const value = Math.max(0, Math.min(100, Number(rawValue) || 0))

  return (
    <Progress
      value={value}
      className={cn(element.className)}
      data-testid={`progress-${element.id}`}
      data-atom-type="progress"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    />
  )
}
